<template>
    <div class="chart-list mb-5 mt-3">
        <div class="card mb-3 table-card">
            <div class="card-header">
                <div class="row">
                    <div class="col-lg-6 col-12">
                        <h3 class="mt-2">Charts ({{ filtered_charts.length }})</h3>
                    </div>

                    <div class="col-lg-6 d-none d-lg-block">
                        <div class="d-flex justify-content-end justify-self-end gap-2">
                            <div class="position-relative">
                                <input 
                                    type="text" 
                                    placeholder="Search by Chart Name..." 
                                    aria-label="Search by Chart Name..." 
                                    v-model="search_key" 
                                    @input="handleFilterSelection" 
                                    autocomplete="one-time-code" 
                                    class="search-box form-control me-1 py-2"
                                >
                                <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                            </div>
                            <button 
                                class="btn btn-secondary me-1 py-0 dropdown-toggle dropdown-toggle-no-btn" 
                                data-bs-toggle="dropdown" 
                                data-bs-auto-close="outside" 
                                aria-expanded="false"
                            >
                                <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters
                            </button>
                            <div class="dropdown-menu dropdown-menu-primary p-3">
                                <p>Client Name:</p>
                                <v-select 
                                    :options="filtered_clients" 
                                    v-model="filters.client"
                                    :searchable="true" 
                                    class="searchable-select"
                                    :selectable="(option) => option.text !== ''"
                                    label="text" 
                                    @search="fetchClientOptions"
                                    @update:modelValue="handleFilterSelection"
                                    :filterable="false"
                                >
                                    <template #selected-option="{ text, category }">
                                        {{ (category != null && category !== '' && text != null && text !== "") ? category + ": " : "" }}{{ text }}
                                    </template>

                                    <template #open-indicator="{ attributes }">
                                        <span v-bind="attributes" style="width: 12px; line-height: 8px;">
                                            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'>
                                                <path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/>
                                            </svg>
                                        </span>
                                    </template>

                                    <template #option="{ text, category }">
                                        <div 
                                            v-if="text === ''" 
                                            :data-category="category" 
                                            class="category-header"
                                            @click="expandCategory(category, null)"
                                            data-isexpanded="false"
                                        >
                                            {{ category }} <i class="fas fa-caret-right"></i>
                                        </div>
                                        <div 
                                            v-else 
                                            class="suboption" 
                                            :data-subcategory="category"
                                            :class="{ 'show': category == null || category === '' }"
                                        >
                                            {{ text }}
                                        </div>
                                    </template>
                                    <template #no-options="{ search, searching, loading }">
                                        <div class="suboption show">
                                            <em>No results found</em>
                                        </div>
                                    </template>
                                </v-select>

                                <hr>

                                <p>Chart Type:</p>
                                <select 
                                    class="form-select" 
                                    v-model="filters.chart_type" 
                                    @change="handleFilterSelection"
                                 aria-label="Chart Type">
                                    <option value="all">*** All Chart Type ***</option>
                                    <option value="100_percent_stacked_column">100% Stacked Column Chart</option>
                                    <option value="100_percent_stacked_bar">100% Stacked Horizontal Bar Chart</option>
                                    <option value="bar_line">Bar-Line Combo Chart</option>
                                    <option value="column">Column Chart</option>
                                    <option value="cumulative_line">Cumulative Line Chart</option>
                                    <option value="horizontal_bar">Horizontal Bar Chart</option>
                                    <option value="information_card">Information Card</option>
                                    <option value="line">Line Chart</option>
                                    <option value="multi_dimension_table">Multi-Dimension Table</option>
                                    <option value="multi_metric_scorecard">Multi-Metric Scorecard</option>
                                    <option value="pie">Pie Chart</option>  
                                    <option value="scorecard">Scorecard</option>
                                    <option value="stacked_bar">Stacked Horizontal Bar Chart</option>
                                    <option value="stacked_column">Stacked Column Chart</option>
                                    <option value="table">Table</option>                                   
                                </select>
                                <hr>

                                <p>Data Source:</p>
                                <select 
                                    class="form-select" 
                                    v-model="filters.data_blend_type" 
                                    @change="handleFilterSelection"
                                 aria-label="Data Blend Type">
                                    <option value="all">*** All Data Source ***</option>
                                    <option value="constituent">Constituent</option>
                                    <option value="engagement">Engagement</option>
                                    <option value="subscriber">Subscriber</option>
                                    <option value="benchmark_constituent">Benchmark Constituent</option>
                                    <option value="benchmark_subscriber">Benchmark Subscriber</option>
                                    <option value="benchmark_wiland">Benchmark Wiland</option>
                                    <option value="benchmark_year_end">Benchmark Year End</option>
                                </select>
                            </div>
                            <a 
                                v-if="filtered_charts.length > 0" 
                                class="btn btn-primary float-end" 
                                href="/admin/chart_builder/new"
                            >
                                <i class="fa fas fa-fw fa-plus"></i> Add Chart
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card-body p-0">
                <table class="table table-with-pagination mb-0">
                    <thead>
                        <tr class="table-header-row">
                            <th @click="sortByKey('name')" class="d-table-cell">
                                <div class="d-flex flex-row">
                                    Name
                                    <div class="d-flex flex-column">
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-up.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'asc' }" alt=""
                                        >
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-down.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'des' }" alt=""
                                        >
                                    </div>
                                </div>
                            </th>

                            <th @click="sortByKey('type')" class="d-table-cell">
                                <div class="d-flex flex-row">
                                    Chart Type
                                    <div class="d-flex flex-column">
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-up.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'type' && sort_order === 'asc' }" alt=""
                                        >
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-down.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'type' && sort_order === 'des' }" alt=""
                                        >
                                    </div>
                                </div>
                            </th>

                            <th @click="sortByKey('data_blend_type')" class="d-table-cell">
                                <div class="d-flex flex-row">
                                    Data Blend Type
                                    <div class="d-flex flex-column">
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-up.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'data_blend_type' && sort_order === 'asc' }" alt=""
                                        >
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-down.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'data_blend_type' && sort_order === 'des' }" alt=""
                                        >
                                    </div>
                                </div>
                            </th>

                            <th @click="sortByKey('client_name')" class="d-table-cell">
                                <div class="d-flex flex-row">
                                    Client
                                    <div class="d-flex flex-column">
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-up.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'client_name' && sort_order === 'asc' }" alt=""
                                        >
                                        <img 
                                            class="sort-key" 
                                            src="/img/icons/avid-icons/chevron-down.svg" 
                                            :class="{ 'sort-key-active': sort_key === 'client_name' && sort_order === 'des' }" alt=""
                                        >
                                    </div>
                                </div>
                            </th>

                            <th class="column-sort-disabled d-table-cell pe-3" width="1">
                                <div class="d-flex flex-row">
                                    Actions
                                </div>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(chart, index) in list_shown" :key="chart.id">
                            <td class="d-table-cell align-start">
                                <a :href="'/admin/chart_builder/' + chart.id">{{ propercase(chart.name) }}</a>
                            </td>

                            <td class="d-table-cell">
                                {{ propercase(chart.type) }}
                            </td>
                            <td class="d-table-cell">
                                {{ propercase(chart.data_blend_type) }}
                            </td>
                            <td class="d-table-cell">
                                {{ (chart.client != null ? chart.client.name : '*** Global Template ***') }}
                            </td>
                            <td class="d-table-cell">
                                <div class="dropdown d-flex justify-content-center">
                                    <button 
                                        class="btn btn-white btn-icon-only-md" 
                                        data-bs-toggle="dropdown" 
                                        data-bs-auto-close="true" 
                                        aria-expanded="false"
                                     aria-label="Button">
                                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                        <a 
                                            class="dropdown-item" 
                                            href="#" 
                                            @click.prevent="copyChart(chart.id)"
                                        >
                                            <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy Chart
                                        </a>
                                        <hr class="my-1">
                                        <a 
                                            class="dropdown-item text-danger" 
                                            href="#" 
                                            @click.prevent="deleteChart(chart)"
                                        >
                                            <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Chart
                                        </a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot class="table-border-bottom-0">
                        <tr>
                            <td colspan="100%" class="py-0">
                                <div class="table-pagination">
                                    <div class="table-pagination--group d-none d-md-flex">
                                        <div class="position-relative">
                                            <select class="form-select" v-model="num_per_page" @change="reloadPagination(filtered_charts)" aria-label="Num Per Page">
                                                <option value="5000">ALL</option>
                                                <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                            </select>
                                            <div class="select-display-text">
                                                Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="table-pagination--group d-none d-lg-flex">
                                        {{ filtered_charts.length ==0 ? '0' : item_interval }} of {{ filtered_charts.length }}
                                    </div>

                                    <div class="table-pagination--group">
                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage(filtered_charts)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                            <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                                        </button>

                                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                            <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click="moveToPage(page, filtered_charts)">
                                                {{ page }}
                                            </button>
                                        </div>
                                        <div class="d-flex page-selector" v-else>
                                            <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click="moveToPage(page, filtered_charts)">
                                                {{ page }}
                                            </button>

                                            <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                            ...
                                            </button>
                                            <button v-if="page_index >= 2 && page_index < page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                                                {{ page_index + 1 }}
                                            </button>

                                            <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                                            ...
                                            </button>
                                            <button :class="page_index === (page_index_array.length -1) ? 'current-page' : ''" class="btn btn-none btn-icon-only-md" @click="moveToPage(page_index_array.length, filtered_charts)" :key="page_index_array.length">
                                                {{ page_index_array.length }}
                                            </button>
                                        </div>

                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage(filtered_charts)" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                                            <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                                        </button>
                                    </div>

                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import vSelect from "vue3-select";
import tablePaginationMixin from '../../../mixins/table_pagination';
import Swal from 'sweetalert2';
import { useNotifications } from '../../../composables/useNotifications';

export default defineComponent({
    name: 'ChartList',
    components: {
        vSelect,
        Swal
    },
    props: {
        charts: {
            type: Array,
            required: true
        }
    },
    mixins: [tablePaginationMixin],
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    data() {
        return {
            local_charts: [],
            filtered_charts: [],
            list_shown: [],
            search_key: '',
            sort_key: 'name',
            sort_order: 'asc',
            filters: {
                client: {
                    category: "",
                    text: "*** All Clients (including Global Templates) ***",
                    value: -2
                },
                chart_type: 'all',
                data_blend_type: 'all'
            },
            filtered_clients: [],
            all_clients: [],
            page: 1,
            per_page: 10,
            total_pages: 1,
            per_page_options: [10, 25, 50, 100]
        };
    },
    watch: {
        charts: {
            handler(newCharts) {
                this.local_charts = [...newCharts];
                this.filtered_charts = [...newCharts];
                this.loadFilterOptions();
                this.handleFilterSelection();
            },
            deep: true
        }
    },
    beforeMount() {
        this.local_charts = [...this.charts];
        this.filtered_charts = [...this.charts];
        this.loadFilterOptions();
        this.getAppliedFilterWithValue();
        this.handleFilterSelection();
        this.reloadPagination(this.filtered_charts);
    },
    mounted() {
        this.reloadPagination(this.filtered_charts);
    },
    updated() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (let i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    tooltipList[index].hide();
                });
            })(i);
        }
    },
    methods: {
        getAppliedFilterWithValue() {
            let params;
            const search = window.location.search;
            if (search !== '' && search.startsWith('?params=')) {
                try {
                    params = JSON.parse(decodeURIComponent(search.slice(8)));
                } catch {
                    params = null;
                }
            }

            if (params) {
                if (params.client_id) {
                    if (params.client_id === -1) {
                        this.filters.client = {
                            category: "",
                            text: "* Global Template *",
                            value: -1
                        };
                    } else {
                        const client = this.all_clients.find(c => c.id === params.client_id);
                        if (client) {
                            this.filters.client = client;
                        }
                    }
                }
                if (params.chart_type) {
                    this.filters.chart_type = params.chart_type;
                }
                if (params.data_blend_type) {
                    this.filters.data_blend_type = params.data_blend_type;
                }
            }
        },
        loadFilterOptions() {
            this.all_clients = [];
            this.local_charts.forEach((chart) => {
                if (chart.client) {
                    const index = this.all_clients.findIndex(client => client.id === chart.client.id);
                    if (index === -1) {
                        const clientOption = {
                            category: "",
                            text: chart.client.name,
                            value: chart.client.id,
                            id: chart.client.id,
                            name: chart.client.name
                        };
                        this.all_clients.push(clientOption);
                    }
                }
            });
            this.all_clients = this.all_clients.sort((a, b) => a.name.localeCompare(b.name));
            
            // Add Global Template option
            const global_option = {
                category: "",
                text: "* Global Template *",
                value: -1,
                id: -1,
                name: "* Global Template *"
            };
            this.all_clients.splice(0, 0, global_option);
            
            // Add All Clients option
            const all_client_option = {
                category: "",
                text: "*** All Clients (including Global Templates) ***",
                value: -2,
                id: -2,
                name: "*** All Clients (including Global Templates) ***"
            };
            this.all_clients.splice(0, 0, all_client_option);
            
            this.filtered_clients = [...this.all_clients];
        },
        fetchClientOptions(search_key) {
            if (search_key === "") {
                this.filtered_clients = [...this.all_clients];
            } else {
                this.filtered_clients = [...this.all_clients.filter(client => 
                    client.name && client.name.toLowerCase().includes(search_key.toLowerCase())
                )];
            }
        },
        handleFilterSelection() {
            let filtered_list = [];

            this.local_charts.forEach(chart => {
                const in_search = this.search_key === '' || 
                    chart.name.toLowerCase().includes(this.search_key.toLowerCase());

                if (in_search) {
                    let if_match_client_name = false;
                    let if_match_chart_type = false;
                    let if_match_data_source = false;

                    // Check client filter
                    if (this.filters.client && this.filters.client.value !== -2) {
                        if (this.filters.client.value === -1) {
                            // Global Template option
                            if_match_client_name = !chart.client;
                        } else {
                            // Specific client option
                            if_match_client_name = chart.client && chart.client.id === this.filters.client.value;
                        }
                    } else {
                        // All Clients option
                        if_match_client_name = true;
                    }

                    // Check chart type filter
                    if (this.filters.chart_type !== 'all') {
                        if (chart.type === this.filters.chart_type) {
                            if_match_chart_type = true;
                        }
                    } else {
                        if_match_chart_type = true;
                    }

                    // Check data source filter
                    if (this.filters.data_blend_type !== 'all') {
                        if (chart.data_blend_type === this.filters.data_blend_type) {
                            if_match_data_source = true;
                        }
                    } else {
                        if_match_data_source = true;
                    }

                    if (if_match_client_name && if_match_chart_type && if_match_data_source) {
                        filtered_list.push(chart);
                    }
                }
            });

            this.filtered_charts = filtered_list;
            this.sortData();
            this.reloadPagination(this.filtered_charts);

            // Update URL with filter parameters
            let filter_obj = {};
            if (this.filters.client && this.filters.client.value !== -2) {
                filter_obj.client_id = this.filters.client.value;
            }
            if (this.filters.chart_type !== 'all') {
                filter_obj.chart_type = this.filters.chart_type;
            }
            if (this.filters.data_blend_type !== 'all') {
                filter_obj.data_blend_type = this.filters.data_blend_type;
            }

            const params = encodeURI(JSON.stringify(filter_obj));
            const newURL = document.location.pathname + '?params=' + params;
            window.history.replaceState({}, {}, newURL);

            // Close dropdown after selection
            const dropdownElementList = [].slice.call(document.querySelectorAll('.dropdown-toggle'));
            const dropdownList = dropdownElementList.map(function (dropdownToggleEl) {
                return new bootstrap.Dropdown(dropdownToggleEl);
            });
            dropdownList.forEach(dropdown => {
                dropdown.hide();
            });
            this.$forceUpdate();
        },
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = (this.sort_order === 'asc') ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }
            this.sortData();
        },
        sortData() {
            if (this.sort_key === 'client_name') {
                this.filtered_charts.sort((a, b) => {
                    if (a.client && b.client) {
                        return this.sort_order === 'asc' ? 
                            a.client.name.localeCompare(b.client.name) : 
                            b.client.name.localeCompare(a.client.name);
                    } else {
                        if (!a.client) return this.sort_order === 'asc' ? -1 : 1;
                        if (!b.client) return this.sort_order === 'asc' ? 1 : -1;
                    }
                });
            } else {
                if (this.sort_order === 'asc') {
                    this.filtered_charts.sort((a, b) => a[this.sort_key].localeCompare(b[this.sort_key]));
                } else {
                    this.filtered_charts.sort((a, b) => b[this.sort_key].localeCompare(a[this.sort_key]));
                }
            }
            this.reloadPagination(this.filtered_charts);
        },
        copyChart(chart_id) {
            Swal.fire({
                title: "Are you sure you want to copy this chart?",
                text: "All filters, metrics, and associated information will be copied.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    window.axios.post('/api/insights/chart/copy/' + chart_id)
                        .then(response => {
                            if (response.status === 200) {
                                window.location = "/admin/chart_builder/" + response.data.chart.id;
                            }
                        });
                }
            });
        },
        deleteChart(chart) {
            Swal.fire({
                title: "Are you sure you want to delete this chart?",
                text: "This chart will be removed from all associated reports.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    window.axios.delete('/api/insights/chart/' + chart.id).then(response => {
                        this.showNotification({
                            text: '<strong>' + chart.name + '</strong> was successfully deleted!'
                        });
                        
                        for (let i = 0; i < this.local_charts.length; i++) {
                            if (this.local_charts[i].id === chart.id) {
                                this.local_charts.splice(i, 1);
                                break;
                            }
                        }
                        this.filtered_charts = [...this.local_charts];
                        this.handleFilterSelection();
                        this.reloadPagination(this.filtered_charts);
                    });
                }
            });
        }
    }
});
</script>
